
import { useWidgetConfig } from '../../context';
import { useTutorialStore } from '../../hooks';
import { TutorialCard } from './TutorialCard';
import { TutorialPlayer } from './TutorialPlayer';
import type { TutorialDefinition } from '../../types';

/**
 * Lists available tutorials grouped by category.
 * When a tutorial is active, the TutorialPlayer overlay takes over.
 */
export function TutorialsTab() {
  const config = useWidgetConfig();
  const tutorials = config.tutorials ?? [];
  const progress = useTutorialStore((s) => s.progress);
  const activeTutorialId = useTutorialStore((s) => s.activeTutorialId);
  const startTutorial = useTutorialStore((s) => s.startTutorial);

  // Group tutorials by category
  const grouped = tutorials.reduce<Record<string, TutorialDefinition[]>>(
    (acc, t) => {
      const cat = t.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(t);
      return acc;
    },
    {},
  );

  const categories = Object.keys(grouped);

  const activeTutorial = tutorials.find((t) => t.id === activeTutorialId);

  const handleStart = (id: string) => {
    const tut = tutorials.find((t) => t.id === id);
    if (tut) {
      startTutorial(id, tut.steps.length);
    }
  };

  return (
    <>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {tutorials.length === 0 && (
          <p
            style={{
              color: 'var(--sw-text-secondary, #9a9ab0)',
              fontSize: '13px',
              textAlign: 'center',
              padding: '32px 0',
            }}
          >
            No tutorials configured yet.
          </p>
        )}

        {categories.map((cat) => (
          <div key={cat}>
            <h3
              style={{
                margin: '0 0 8px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--sw-text-secondary, #9a9ab0)',
              }}
            >
              {cat}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {grouped[cat].map((tut) => (
                <TutorialCard
                  key={tut.id}
                  tutorial={tut}
                  progress={progress[tut.id]}
                  onStart={handleStart}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Tutorial player overlay — renders outside the panel via portal */}
      {activeTutorial && <TutorialPlayer tutorial={activeTutorial} />}
    </>
  );
}
